import { useState, useEffect, useCallback } from 'react';
import { usePermissionsStore } from '@/stores/permissions';
import { listProjectPlans, searchDocuments, listDocuments, saveDocumentToPlans } from '@/api/tasks';
import type { SharePointItem } from '@/types/odoo';
import { isPlanSaved } from './planSaved';

/* ─── Section: Verlegschema ─── */

const SP_BASE = 'https://magnumheatinggroup.sharepoint.com/sites/THDT/Gedeelde%20documenten/';

function getSharePointUrl(itemPath: string): string {
  const relativePath = itemPath.replace(/^Gedeelde documenten\//, '');
  return SP_BASE + relativePath.split('/').map(encodeURIComponent).join('/');
}

const VERLEGSCHEMA_ARCHIVE_REGEX = /\b(oud|oude|old|archive|archief)\b/i;
const VERLEGSCHEMA_SUBFOLDER_LIMIT = 15;

interface VerlegschemaGroup {
  name: string;
  path: string;
  files: SharePointItem[];
}

function isVerlegschemaArchiveFolder(name: string): boolean {
  return VERLEGSCHEMA_ARCHIVE_REGEX.test(name.trim());
}

function isPdfFile(item: SharePointItem): boolean {
  return item.type === 'file' && item.name.trim().toLowerCase().endsWith('.pdf');
}

export function VerlegschemaSection({ projectId, onSaved }: { projectId: number; onSaved?: () => void }) {
  const canTab = usePermissionsStore((s) => s.canTab);
  const hasPermission = canTab('documenten');

  const [rootFiles, setRootFiles] = useState<SharePointItem[]>([]);
  const [groups, setGroups] = useState<VerlegschemaGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partialError, setPartialError] = useState<string | null>(null);
  const [found, setFound] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!hasPermission) {
      setRootFiles([]);
      setGroups([]);
      setSavedKeys(new Set());
      setPartialError(null);
      setFound(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function loadVerlegschema() {
      setLoading(true);
      setError(null);
      setPartialError(null);
      setRootFiles([]);
      setGroups([]);
      setSavedKeys(new Set());
      setFound(false);
      try {
        // Fetch SharePoint root and existing grondplannen in parallel
        const [rootResult, plansResult] = await Promise.all([
          searchDocuments(projectId),
          listProjectPlans(projectId),
        ]);
        if (cancelled) return;

        if (!rootResult.found || !rootResult.items) {
          setFound(false);
          return;
        }

        const verlegFolder = rootResult.items.find(
          (item) => (
            item.type === 'folder'
            && item.name.trim().toLowerCase().includes('verleg')
            && !isVerlegschemaArchiveFolder(item.name)
          ),
        );
        if (!verlegFolder) {
          setFound(false);
          return;
        }

        const folderResult = await listDocuments(projectId, verlegFolder.path);
        if (cancelled) return;

        const folderItems = folderResult.items ?? [];
        const directPdfs = folderItems.filter(isPdfFile);
        const subfolders = folderItems.filter(
          (item) => item.type === 'folder' && !isVerlegschemaArchiveFolder(item.name),
        );

        const cappedSubfolders = subfolders.slice(0, VERLEGSCHEMA_SUBFOLDER_LIMIT);
        const subfolderResults = await Promise.allSettled(
          cappedSubfolders.map((folder) => listDocuments(projectId, folder.path)),
        );
        if (cancelled) return;

        const warnings: string[] = [];
        if (subfolders.length > cappedSubfolders.length) {
          warnings.push('Slechts eerste 15 submappen geladen');
        }

        const rejectedCount = subfolderResults.filter((result) => result.status === 'rejected').length;
        if (directPdfs.length === 0 && cappedSubfolders.length > 0 && rejectedCount === cappedSubfolders.length) {
          setError('Verlegschema submappen konden niet geladen worden');
          setFound(false);
          return;
        }
        if (rejectedCount > 0) {
          warnings.push('Sommige submappen konden niet geladen worden');
        }

        const pdfGroups = subfolderResults.flatMap((result, index): VerlegschemaGroup[] => {
          if (result.status === 'rejected') return [];
          const folder = cappedSubfolders[index];
          const files = (result.value.items ?? []).filter(isPdfFile);
          if (files.length === 0) return [];
          return [{ name: folder.name, path: folder.path, files }];
        });

        // Check which files are already saved by matching sanitized filenames against plan filenames
        // Plan keys have a timestamp prefix: {timestamp}-{sanitizedFilename} — strip it
        const planFilenames = new Set(
          (plansResult.plans ?? []).map((p: { key: string }) => {
            const name = p.key.split('/').pop() || '';
            return name.replace(/^\d+-/, '');
          }),
        );
        const alreadySaved = new Set<string>();
        for (const file of [...directPdfs, ...pdfGroups.flatMap((group) => group.files)]) {
          if (isPlanSaved(file.name, planFilenames)) {
            alreadySaved.add(file.path);
          }
        }
        setSavedKeys(alreadySaved);

        setRootFiles(directPdfs);
        setGroups(pdfGroups);
        setPartialError(warnings.length > 0 ? warnings.join(' ') : null);
        setFound(directPdfs.length > 0 || pdfGroups.length > 0);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Verlegschema laden mislukt');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadVerlegschema();
    return () => {
      cancelled = true;
    };
  }, [projectId, hasPermission]);

  const handleSave = useCallback(
    async (item: SharePointItem) => {
      setSavingKey(item.path);
      setSaveError(null);
      try {
        await saveDocumentToPlans(projectId, item.path, item.name);
        setSavedKeys((prev) => new Set(prev).add(item.path));
        onSaved?.();
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Opslaan mislukt');
      } finally {
        setSavingKey(null);
      }
    },
    [projectId, onSaved],
  );

  if (!hasPermission) return null;
  if (loading) {
    return (
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-extrabold text-td-navy mb-4">Verlegschema</h3>
        <div className="flex items-center justify-center py-6">
          <div className="w-6 h-6 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Laden...</span>
          </div>
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-extrabold text-td-navy mb-4">Verlegschema</h3>
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
      </section>
    );
  }
  if (!found) return null;

  const formatSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('nl-BE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const allFiles = [...rootFiles, ...groups.flatMap((group) => group.files)];

  const renderRow = (item: SharePointItem) => (
    <div
      key={item.path}
      className="flex items-center gap-3 rounded-lg border border-gray-100 p-3"
    >
      <svg className="w-5 h-5 text-td-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-td-navy truncate">{item.name}</p>
        <p className="text-xs text-td-muted">
          {[formatSize(item.size), formatDate(item.lastModified)].filter(Boolean).join(' \u00b7 ')}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => window.open(getSharePointUrl(item.path), '_blank')}
          className="px-3 py-1.5 text-xs font-bold text-td-blue hover:text-td-blue/80 transition-colors"
        >
          Open
        </button>
        {savedKeys.has(item.path) ? (
          <span className="px-3 py-1.5 text-xs font-bold text-green-700 bg-green-100 border border-green-300 rounded-lg cursor-default flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            Opgeslagen
          </span>
        ) : (
          <button
            type="button"
            onClick={() => handleSave(item)}
            disabled={savingKey === item.path}
            className="px-3 py-1.5 text-xs font-bold text-td-navy bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            {savingKey === item.path && (
              <div className="w-3 h-3 border-2 border-td-navy/30 border-t-td-navy rounded-full animate-spin" />
            )}
            Opslaan
          </button>
        )}
      </div>
    </div>
  );

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h3 className="text-sm font-extrabold text-td-navy mb-4">Verlegschema</h3>

      {allFiles.some((f) => !savedKeys.has(f.path)) && (
        <div className="mb-3 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <svg className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-blue-800">
            Onderstaande bestanden staan in SharePoint. Klik &lsquo;Opslaan&rsquo; om ze zichtbaar te maken voor installateurs.
          </p>
        </div>
      )}

      {partialError && (
        <div className="mb-3 bg-amber-50 text-amber-800 border border-amber-200 px-3 py-2 rounded-lg text-sm">
          {partialError}
        </div>
      )}

      {saveError && (
        <div className="mb-3 bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm flex items-center justify-between">
          <span>{saveError}</span>
          <button type="button" onClick={() => setSaveError(null)} className="text-red-400 hover:text-red-600 ml-2 font-bold">&times;</button>
        </div>
      )}

      <div className="space-y-2">
        {rootFiles.map(renderRow)}
        {groups.map((group) => (
          <div key={group.path} className="pt-3 first:pt-0">
            <h4 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-2">{group.name}</h4>
            <div className="space-y-2">
              {group.files.map(renderRow)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
